import React, { useEffect, useMemo, useRef, useState } from "react";

import { DataTable, DataTableValue } from "primereact/datatable";
import { Column } from "primereact/column";

import PrimeReact from "primereact/api";

import { ColumnGroup } from "primereact/columngroup";
import { Row } from "primereact/row";

import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Tooltip } from "primereact/tooltip";

import { useHistory, useLocation } from "react-router-dom";
import { useToast } from "../../../hooks/toast";
import { Container } from "./styles";
import api from "../../../services/api";
import { totalizarSelecao } from "../../../utils/hourSelectionTotals";

type LinhaComMinutos = IWorkingHours & {
  mins: number;
  /* Vem da API e não estava declarado em IWorkingHours; alimenta a data da
     última atualização mostrada no topo da tela. */
  horas_data_alteracao_original: string | number | Date;
};

interface IWorkingHours {
  horas_id: number;
  horas_cod_cliente: number;
  horas_cod_referencia: number;
  horas_cod_responsavel: number;
  horas_descricao: string;
  horas_data: Date | undefined;
  horas_hora_inicial: Date | undefined;
  horas_hora_final: Date | undefined;
  horas_valor_hora: number;
  horas_documento: string;
  horas_data_alteracao: Date | undefined;
  horas_hora_total: string;
  horas_documento_seq: number;
  horas_processo_id: number;
  horas_reg: string;
  horas_num_pasta: string;
  horas_autor: string;
  horas_reu: string;
  horas_fatu_de: Date | undefined;
  horas_fatu_ate: Date | undefined;
  horas_fatu_ajuste: number;
  num_seq: number;
  valor_ajuste_nao_faturado: number;
  tipo_usuario: string;
  tipo_horas: string;
  horas_hora_inicial_texto: String;
  horas_hora_final_texto: String;
  usuario_logado: number;
  responsavel_nome?: string;
  advogado_nome?: string;
  administrador_nome?: string;
}

// === TIME HELPERS ===
const pad2 = (n: number) => String(n).padStart(2, "0");

// Aceita: "HH:MM", "H:M", "H:7.8" (legado), "0,22" ou "0.22" (horas decimais)
const parseFlexibleToMinutes = (val?: string | null): number => {
  if (!val) return 0;
  const s = String(val).trim();

  // Possui "H:…"
  if (s.includes(":")) {
    const [hStr = "0", mRaw = "0"] = s.split(":");
    const h = Number.parseInt(hStr, 10) || 0;

    let m: number;
    if (mRaw.includes(".") || mRaw.includes(",")) {
      // FORMATO LEGADO "H:7.8"  -> minutos ≈ round( 7.8 * 100 / 60 ) = 13
      const frac = Number.parseFloat(mRaw.replace(",", "."));
      m = Number.isNaN(frac) ? 0 : Math.round((frac * 100) / 60);
    } else {
      m = Number.parseInt(mRaw, 10) || 0;
    }

    // carry se m >= 60
    return h * 60 + m;
  }

  // Horas decimais puras ("0,22"/"0.22")
  const dec = Number.parseFloat(s.replace(",", "."));
  if (!Number.isNaN(dec)) return Math.round(dec * 60);

  return 0;
};

const minutesToHHMM = (total: number): string => {
  const h = Math.floor(total / 60);
  const m = total % 60;
  return `${pad2(h)}:${pad2(m)}`; // horas com zero à esquerda p/ bater com tela
};

const WorkingHour: React.FC = () => {
  // active ripple effect
  PrimeReact.ripple = true;

  const loggedUser: number = sessionStorage.getItem("usuario_logado")
    ? Number(sessionStorage.getItem("usuario_logado"))
    : 0;

  const initialValues: IWorkingHours = {
    horas_id: 0,
    horas_cod_cliente: 0,
    horas_cod_referencia: 0,
    horas_cod_responsavel: 0,
    horas_descricao: "",
    horas_data: new Date(),
    horas_hora_inicial: new Date(),
    horas_hora_final: undefined,
    horas_valor_hora: 0,
    horas_documento: "",
    horas_data_alteracao: undefined,
    horas_hora_total: "00:00",
    horas_documento_seq: 0,
    horas_processo_id: 0,
    horas_reg: "",
    horas_num_pasta: "",
    horas_autor: "",
    horas_reu: "",
    horas_fatu_de: undefined,
    horas_fatu_ate: undefined,
    horas_fatu_ajuste: 0,
    num_seq: 0,
    valor_ajuste_nao_faturado: 0,
    tipo_usuario: "adv",
    tipo_horas: "normais",
    horas_hora_inicial_texto: "",
    horas_hora_final_texto: "",
    usuario_logado: loggedUser,
  };

  const [workingHours, setWorkingHours] = useState<IWorkingHours[]>([]);
  const [workingHour, setWorkingHour] = useState(initialValues);
  const [deleteHourDialog, setDeleteHourDialog] = useState(false);
  const [selectedHours, setSelectedHours] = useState<IWorkingHours[]>([]);
  const [latestUpdateDate, setLatestUpdateDate] = useState("");
  // para tabela
  const [loading, setLoading] = useState(true);
  const [globalFilter, setGlobalFilter] = useState("");
  const dt = useRef(null);

  const [notifyCustomer, setNotifyCustomer] = useState<boolean>(false);
  const [notifyAttorney, setNotifyAttorney] = useState<boolean>(false);
  const [otherEmails, setOtherEmails] = useState("");
  const [extraInformation, setExtraInformation] = useState("");

  const { addToast } = useToast();
  const history = useHistory();
  const location = useLocation();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const state = location.state as any;
  let choice = "";

  if (state) {
    choice = ` ${state.tipo_horas}`;
  }

  const [totalTime, setTotalTime] = useState("");
  const [totalDecimalTime, setTotalDecimalTime] = useState("");

  useEffect(() => {
    (async () => {
      await api
        .post("/management/workinghours/general/filterbytype", state)
        .then((response) => {
          const rows: LinhaComMinutos[] = response.data.map(
              (r: IWorkingHours) => {
            const mins = parseFlexibleToMinutes(r.horas_hora_total || "0:00");
            return {
              ...r,
              mins,
            };
          });

          setWorkingHours(rows);

          const maxUpdatedDate = new Date(
            Math.max(
              ...rows.map((el) =>
                new Date(el.horas_data_alteracao_original).getTime()
              )
            )
          );
          setLatestUpdateDate(
            maxUpdatedDate.toLocaleDateString("pt-BR", { timeZone: "UTC" })
          );

          const totalMinutes = rows.reduce(
            (acc: number, r: LinhaComMinutos) => acc + (r.mins ?? 0),
            0
          );

          setTotalTime(minutesToHHMM(totalMinutes));
          setTotalDecimalTime(
            (totalMinutes / 60).toLocaleString("pt-BR", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
              minimumIntegerDigits: 1,
            })
          );
        });

      setLoading(false);
    })();
  }, [state]);

  const hourBodyTemplate = (rowData: LinhaComMinutos) => {
    const minutes =
      rowData.mins ?? parseFlexibleToMinutes(rowData.horas_hora_total);
    return minutesToHHMM(minutes);
  };

  const decimalBodyTemplate = (rowData: LinhaComMinutos) => {
    const minutes =
      rowData.mins ?? parseFlexibleToMinutes(rowData.horas_hora_total);
    return (minutes / 60).toLocaleString("pt-BR", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      minimumIntegerDigits: 1,
    });
  };

  const checkboxBodyTemplate = (rowData: { horas_id: number }) => {
    if (workingHours !== undefined) {
      <Checkbox
        inputId={`${rowData.horas_id.toString()}`}
        name={`cb${rowData.horas_id.toString()}`}
        value={selectedHours}
        checked={false}
      />;
      rowData.horas_id.toString();
    }
  };

  const nameBodyTemplate = (rowData: {
    horas_cod_responsavel: number;
    responsavel_nome?: string;
    advogado_nome: string;
    administrador_nome: string;
  }) =>
    rowData.responsavel_nome ||
    rowData.advogado_nome ||
    rowData.administrador_nome ||
    "";

  const selectionTotals = useMemo(
    () =>
      totalizarSelecao(
        selectedHours.map((row) => ({
          minutos: parseFlexibleToMinutes(row.horas_hora_total),
          valorHora: row.horas_valor_hora,
          ajusteEmHoras: row.valor_ajuste_nao_faturado,
        }))
      ),
    [selectedHours]
  );

  const semValorDefinido =
    selectionTotals.linhasSemValor === selectionTotals.quantidade;

  const mutedFooter = { fontWeight: "normal" as const, opacity: 0.75 };

  // 10 colunas visíveis: cada linha do rodapé precisa somar 10.
  const footerGroup = (
    <ColumnGroup>
      {selectionTotals.quantidade > 0 ? (
        <Row>
          <Column
            footer={`Horas selecionadas (${selectionTotals.quantidade}):`}
            colSpan={6}
            footerStyle={{ textAlign: "right" }}
          />
          <Column
            footer={minutesToHHMM(selectionTotals.minutos)}
            footerStyle={{ textAlign: "center" }}
          />
          <Column
            footer={selectionTotals.decimalNaTela}
            footerStyle={{ textAlign: "center" }}
          />
          <Column
            footer={
              semValorDefinido
                ? "valor da hora não definido"
                : selectionTotals.valorFormatado
            }
            colSpan={2}
            footerStyle={
              semValorDefinido
                ? { textAlign: "center", ...mutedFooter }
                : { textAlign: "center" }
            }
          />
        </Row>
      ) : null}
      {!semValorDefinido && selectionTotals.linhasSemValor > 0 ? (
        <Row>
          <Column colSpan={8} />
          <Column
            footer={`${selectionTotals.linhasSemValor} de ${selectionTotals.quantidade} sem valor definido`}
            colSpan={2}
            footerStyle={{ textAlign: "center", ...mutedFooter }}
          />
        </Row>
      ) : null}
      {selectionTotals.quantidade > 0 && selectionTotals.divergem ? (
        <Row>
          <Column
            footer="Sem arredondar cada linha:"
            colSpan={6}
            footerStyle={{ textAlign: "right", ...mutedFooter }}
          />
          <Column />
          <Column
            footer={selectionTotals.decimalExato}
            footerStyle={{ textAlign: "center", ...mutedFooter }}
          />
          <Column colSpan={2} />
        </Row>
      ) : null}
      <Row>
        <Column
          footer="Total de horas:"
          colSpan={6}
          footerStyle={{ textAlign: "right" }}
        />
        <Column footer={totalTime} footerStyle={{ textAlign: "center" }} />
        <Column
          footer={totalDecimalTime}
          footerStyle={{ textAlign: "center" }}
        />
        <Column colSpan={2} />
      </Row>
    </ColumnGroup>
  );

  const handleCreate = () => {
    history.push("/management/workinghour/create");
  };

  const hidedeleteHourDialog = () => {
    setDeleteHourDialog(false);
  };

  const editHour = (rowData: DataTableValue) => {
    history.push(`/management/workinghour/edit/${rowData.horas_id}`);
  };

  const confirmDeleteHour = (
    horaSelecionada: React.SetStateAction<IWorkingHours>
  ) => {
    setWorkingHour(horaSelecionada);
    setDeleteHourDialog(true);
  };

  const deleteHour = () => {
    if (choice && choice === " pendentes") {
      api.delete(
        `/management/workinghours/delete/${workingHour.horas_id}/true`
      );
    } else {
      api.delete(
        `/management/workinghours/delete/${workingHour.horas_id}/false`
      );
    }
    setDeleteHourDialog(false);

    const updatedRecords = workingHours.filter(
      (val) => val.horas_id !== workingHour.horas_id
    );
    setWorkingHours(updatedRecords);

    addToast({
      type: "success",
      title: "Cadastro excluído!",
      description: "O cadastro selecionado foi excluído com sucesso!",
    });
  };

  const changeHourStatus = async (rowData: {
    horas_id: number;
    status_hora: number;
  }) => {
    await api.patch(
      `management/pendingworkinghours/patch/hourstatus/${rowData.horas_id}`
    );
    await api
      .post("/management/workinghours/general/filterbytype", state)
      .then((response) => {
        setWorkingHours(response.data);
      });
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const duplicateHour = (rowData: any) => {
    localStorage.removeItem("@Jurisnet:workinghour");
    localStorage.setItem("@Jurisnet:workinghour", JSON.stringify(rowData));
    history.push("/management/workinghour/duplicate");
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const actionBodyTemplate = (rowData: any) => (
    <>
      {choice && choice === " normais" ? (
        <>
          <Button
            type="button"
            icon="pi pi-pencil"
            tooltip="Alterar cadastro"
            tooltipOptions={{ position: "left" }}
            className="p-button-rounded p-button-text p-button-plain p-mr-2"
            onClick={() => {
              editHour(rowData);
            }}
          />
          <Button
            type="button"
            icon="pi pi-clone"
            className="p-button-rounded p-button-text p-button-plain p-mr-2"
            onClick={() => {
              duplicateHour(rowData);
            }}
            tooltip="Duplicar este cadastro"
            tooltipOptions={{ position: "left" }}
          />
        </>
      ) : null}
      <Button
        type="button"
        icon="pi pi-trash"
        tooltip="Excluir cadastro"
        tooltipOptions={{ className: "red-tooltip", position: "left" }}
        className="p-button-rounded p-button-text p-button-plain p-mr-2"
        onClick={() => {
          confirmDeleteHour(rowData);
        }}
      />
      {choice && choice === " pendentes" ? (
        <Button
          type="button"
          icon={
            rowData.status_hora === 0
              ? "pi pi-stop-circle"
              : "pi pi-thumbs-down"
          }
          className={
            rowData.status_hora === 0
              ? "p-button-rounded p-button-text p-button-plain p-button-danger"
              : "p-button-rounded p-button-text p-button-plain p-button-info"
          }
          onClick={() => {
            changeHourStatus(rowData);
          }}
          tooltip={`Clique para ${
            rowData.status_hora === 0 ? "aprovar" : "reprovar"
          } o cadastro`}
          tooltipOptions={{ position: "left" }}
        />
      ) : null}
    </>
  );

  const searchHours = () => {
    history.push("/management/workinghour/search");
  };

  const header = (
    <div className="flex align-items-center export-buttons">
      {choice && choice === " normais" ? (
        <Button
          type="button"
          label="Novo"
          icon="pi pi-plus"
          className="p-button-secondary p-mr-2"
          onClick={handleCreate}
          tooltip="Novo cadastro"
          tooltipOptions={{ position: "right" }}
        />
      ) : null}
      <Button
        icon="pi pi-search"
        iconPos="left"
        label="Consultar horas"
        className="p-button p-component p-button-secondary p-mr-2"
        type="button"
        onClick={searchHours}
      />
      <span className="p-input-icon-left p-ml-auto">
        <i className="pi pi-search" />
        <InputText
          type="search"
          value={globalFilter}
          onChange={(e) => setGlobalFilter(e.target.value)}
          placeholder="Pesquisar na tabela"
        />
      </span>
    </div>
  );

  const paginatorLeft = (
    <Button type="button" icon="pi pi-refresh" className="p-button-text" />
  );
  const paginatorRight = (
    <Button type="button" icon="pi pi-cloud" className="p-button-text" />
  );

  const deleteHourDialogFooter = (
    <>
      <Button
        type="button"
        label="Não"
        icon="pi pi-times"
        className="p-button-text"
        onClick={hidedeleteHourDialog}
      />
      <Button
        type="button"
        label="Sim"
        icon="pi pi-check"
        className="p-button-text"
        onClick={deleteHour}
      />
    </>
  );

  const handleNotificationEmails = () => {
    try {
      let hourID = "";

      if (selectedHours) {
        selectedHours.forEach((item: { horas_id: number }) => {
          if (hourID === "") {
            hourID = item.horas_id.toString();
          } else {
            hourID += `,${item.horas_id.toString()}`;
          }
        });
      }

      api.post("/management/workinghours/sendmail", {
        otherEmails,
        extraInformation,
        hourID,
        loggedUser,
        notifyCustomer,
        notifyAttorney,
      });

      addToast({
        type: "success",
        title: "E-mail(s) enviado(s) com sucesso",
        description:
          "O(s) e-mails selecionados para envio foram processados com sucesso.",
      });
    } catch (err) {
      addToast({
        type: "error",
        title: "Erro ao enviar o email",
        description: "Ocorreu um erro ao enviar o e-mail. Tente novamente.",
      });
    }
  };

  return (
    <Container>
      <header>
        <div>
          <h1>
            Horas cadastradas
            {choice}
          </h1>
        </div>
      </header>

      <div className="datatable-responsive">
        <div className="card">
          <Tooltip target=".export-buttons>button" position="bottom" />

          <DataTable
            tableClassName="table-workinghours table"
            id="table-workinghours"
            ref={dt}
            loading={loading}
            value={workingHours}
            editMode="row"
            dataKey="horas_id"
            header={header}
            stripedRows
            className="p-datatable-sm"
            paginator
            paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
            currentPageReportTemplate="Exibindo {first} a {last} de {totalRecords}"
            rows={workingHours.length}
            rowsPerPageOptions={[10, 20, 50, workingHours.length]}
            paginatorLeft={paginatorLeft}
            paginatorRight={paginatorRight}
            globalFilter={globalFilter}
            emptyMessage="Nenhum registro encontrado."
            selectionMode="checkbox"
            selection={selectedHours}
            onSelectionChange={(e) => {
              const selHours = e.value as IWorkingHours[];
              setSelectedHours(selHours);
            }}
            onRowDoubleClick={(e) => editHour(e.data)}
            footerColumnGroup={footerGroup}
          >
            <Column
              key="hora_data"
              field="hora_data"
              header="Data"
              alignHeader="center"
              align="left"
              style={{ width: "100px" }}
            />
            <Column
              field="cliente_nome"
              header="Cliente"
              alignHeader="center"
              align="left"
              style={{ width: "140px" }}
            />
            <Column
              field="horas_referencia_nome"
              header="Referência"
              alignHeader="center"
              align="left"
              style={{ width: "80px" }}
            />
            <Column
              field="advogado_nome"
              header="Responsável"
              alignHeader="center"
              body={nameBodyTemplate}
              align="left"
              style={{ width: "120px" }}
            />
            <Column
              field="horas_descricao"
              header="Descrição"
              alignHeader="center"
              align="left"
            />
            <Column
              field="horas_tipo"
              header="Tipo"
              alignHeader="center"
              align="left"
              style={{ width: "100px" }}
            />
            <Column
              field="_horas_fmt"
              header="Horas"
              alignHeader="center"
              body={hourBodyTemplate}
              align="center"
              style={{ width: "80px" }}
            />
            <Column
              field="_decimal_fmt"
              header="Decimais"
              alignHeader="center"
              body={decimalBodyTemplate}
              align="center"
              style={{ width: "80px" }}
            />
            <Column
              field="status"
              header="Status"
              alignHeader="center"
              align="left"
              style={{ width: "120px" }}
              hidden={choice !== " pendentes"}
            />
            <Column
              key="horas_id"
              field="horas_id"
              header="Enviar Inf."
              alignHeader="center"
              body={checkboxBodyTemplate}
              selectionMode="multiple"
              sortable={false}
              align="center"
              style={{ width: "75px" }}
              hidden={choice !== " normais"}
            />
            <Column
              body={actionBodyTemplate}
              align="right"
              style={{ width: "180px" }}
            />
          </DataTable>
          <div className="rigthDiv">
            <strong>Data última atualização:&nbsp;</strong>
            {latestUpdateDate}
          </div>

          {choice === " normais" ? (
            <>
              <div className="col-12 p-mt-20">
                <header>
                  <h3>Enviar cópias de e-mail</h3>
                </header>

                <div className="field grid p-mt-20">
                  <div className="col-12 md:col-6">
                    <Checkbox
                      inputId="avisar_cliente"
                      name="avisar_cliente"
                      onChange={(e) => {
                        if (e.checked !== undefined) {
                          setNotifyCustomer(e.checked);
                        }
                      }}
                      checked={notifyCustomer}
                    />
                    <label htmlFor="avisar_cliente"> Avisar cliente</label>
                  </div>
                  <div className="col-12 md:col-6">
                    <Checkbox
                      inputId="avisar_advogado"
                      name="avisar_advogado"
                      onChange={(e) => {
                        if (e.checked !== undefined) {
                          setNotifyAttorney(e.checked);
                        }
                      }}
                      checked={notifyAttorney}
                    />
                    <label htmlFor="avisar_advogado"> Avisar advogado</label>
                  </div>
                </div>

                <div className="col-12 md:col-12">
                  <label htmlFor="copia_emails_outros">
                    Enviar cópia para e-mails adicionais (separe os emails por
                    ponto-e-vírgula &quot; ; &quot;)
                  </label>
                  <InputText
                    id="copia_emails_outros"
                    name="copia_emails_outros"
                    className="inputfield w-full"
                    defaultValue={otherEmails}
                    onChange={(e) => setOtherEmails(e.target.value)}
                  />
                </div>
                <div className="col-12 md:col-12">
                  <label htmlFor="emails_observacoes">Observações</label>
                  <InputTextarea
                    id="emails_observacoes"
                    name="emails_observacoes"
                    className="inputfield w-full"
                    value={extraInformation}
                    onChange={(e) => setExtraInformation(e.target.value)}
                    rows={5}
                    cols={30}
                  />
                </div>
              </div>
              <div className="col-12">
                <div className="field grid">
                  <div className="col-12 md:col-12">
                    <div className="flex align-content-center justify-content-center field">
                      <Button
                        icon="pi pi-check"
                        iconPos="left"
                        label="Enviar e-mail"
                        className="p-mr-2"
                        type="submit"
                        onClick={handleNotificationEmails}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </>
          ) : null}
        </div>

        <div>
          <Dialog
            visible={deleteHourDialog}
            style={{ width: "450px" }}
            header="Confirmação"
            modal
            footer={deleteHourDialogFooter}
            onHide={hidedeleteHourDialog}
          >
            <div className="confirmation-content">
              <i
                className="pi pi-exclamation-triangle p-mr-3"
                style={{ fontSize: "2rem" }}
              />
              {workingHour && (
                <span>
                  Você tem certeza que deseja excluir
                  <br />
                  <br />
                  <b>{workingHour.horas_descricao}</b>?
                </span>
              )}
            </div>
          </Dialog>
        </div>
      </div>
    </Container>
  );
};

export default WorkingHour;
